import { useEffect, useRef, useState } from 'react';
import { Printer, X, AlertTriangle } from 'lucide-react';
import { saleDateLabel, saleMoney, saleStatusLabel } from '@klothiq/domain';
import WorkModalShell from '@/components/common/WorkModalShell';
import BrandLogo from '@/components/common/BrandLogo';
import { brandConfig } from '@/config/brandConfig';
import type { PackingSlip } from '@/lib/packingSlips';
import { printHtmlContent } from '@/utils/printUtils';
import { customerPrintHtml } from '@/utils/customerPrintPrivacy';
import { usePrintPrivacy } from '@/hooks/usePrintPrivacy';

export default function SalesPackingSlips({
  slips,
  onClose,
}: {
  slips: PackingSlip[];
  onClose: () => void;
}) {
  const [printing, setPrinting] = useState(false);
  const { askPrivacy, privacyDialog } = usePrintPrivacy();
  const printOperation = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      printOperation.current?.abort();
    };
  }, []);
  const incomplete = slips.filter(
    (slip) => !slip.recipient.name || !slip.recipient.phone || !slip.recipient.address,
  ).length;
  const print = async () => {
    const masked = await askPrivacy();
    if (masked === null || !mounted.current) return;
    const content = document.getElementById('selected-sales-packing-slips');
    if (!content || printing || printOperation.current) return;
    const controller = new AbortController();
    printOperation.current = controller;
    printHtmlContent(customerPrintHtml(content, masked), {
      documentTitle: `${brandConfig.name} packing slips`,
      signal: controller.signal,
      onBeforePrint: () => setPrinting(true),
      onAfterPrint: () => {
        printOperation.current = null;
        if (mounted.current) setPrinting(false);
      },
    });
  };
  return (
    <>
      <WorkModalShell open title="Courier packing slips" onClose={onClose} busy={printing} wide>
        <header className="flex items-start justify-between gap-3 border-b border-border py-3">
          <div>
            <h2 className="text-lg font-semibold">Courier packing slips</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {slips.length} selected sales · A4 portrait · multiple slips per sheet · cut along
              dashed lines
            </p>
          </div>
          <button
            type="button"
            aria-label="Close packing slips"
            disabled={printing}
            onClick={onClose}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-muted disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto py-4">
          {incomplete > 0 && (
            <p
              role="status"
              className="flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {incomplete} {incomplete === 1 ? 'slip has' : 'slips have'} missing recipient details.
              Review the fields marked Not recorded before printing.
            </p>
          )}
          <div
            id="selected-sales-packing-slips"
            style={{ color: '#0f172a', fontFamily: 'Arial, sans-serif' }}
          >
            <style>{`
            .courier-slip{background:white;border:1px solid #dbe1ea;border-radius:10px;padding:12px;margin-bottom:12px;overflow-wrap:anywhere;font-size:11px;line-height:1.35}
            .courier-slip p{margin:0}.courier-slip header{display:flex;align-items:center;gap:8px;border-bottom:1px solid #c7a13f;padding-bottom:6px}
            .courier-slip .courier-brand{flex:1;min-width:0}.courier-slip h3{margin:0;font-size:14px;font-weight:bold}
            .courier-slip .courier-brand p,.courier-slip .courier-reference p{font-size:9px;color:#475569}
            .courier-slip .courier-reference{text-align:right;max-width:45%}.courier-slip .courier-reference strong{font-size:12px}
            .courier-slip .courier-sender{font-size:9px;color:#475569;margin-top:4px}
            .courier-slip .courier-recipient{border-left:2px solid #c7a13f;background:#f8fafc;padding:5px 8px;margin:6px 0}
            .courier-slip .courier-contact{display:flex;justify-content:space-between;flex-wrap:wrap;gap:2px 10px}
            .courier-slip .courier-name{font-size:12px;font-weight:bold}.courier-slip address{font-style:normal;white-space:pre-wrap;margin-top:2px}
            .courier-slip table{width:100%;border-collapse:collapse;table-layout:fixed;font-size:10px}.courier-slip th{background:#f1f5f9;text-align:left}
            .courier-slip th,.courier-slip td{padding:3px 5px;border:1px solid #dbe1ea;vertical-align:top}
            .courier-slip .courier-product{width:50%}.courier-slip .courier-qty{width:6%;text-align:center}.courier-slip .courier-money{width:14.66%;text-align:right;white-space:nowrap}
            .courier-slip .courier-variant{font-size:9px;color:#475569;margin-top:1px}
            .courier-slip .courier-totals{display:flex;justify-content:flex-end;flex-wrap:wrap;gap:4px 12px;margin-top:6px;padding-top:4px;border-top:1px solid #dbe1ea;break-inside:avoid}
            .courier-slip .courier-totals div{display:flex;gap:5px}.courier-slip .courier-totals dt{color:#475569}.courier-slip .courier-totals dd{margin:0;font-weight:bold;white-space:nowrap}
            .courier-slip .courier-grand-total{color:#0f172a;font-size:12px}.courier-slip .courier-grand-total dt{color:inherit;font-weight:bold}
            .courier-slip footer{font-size:8px;color:#475569;margin-top:4px;break-inside:avoid}
            .courier-slip .courier-cut{display:flex;align-items:center;gap:8px;color:#64748b;font-size:8px;margin-top:10px;break-inside:avoid}
            .courier-slip .courier-cut::before,.courier-slip .courier-cut::after{content:'';flex:1;border-top:1px dashed #64748b}
            @media screen and (max-width:639px){
              .courier-slip .courier-brand p{max-width:110px}.courier-slip .courier-reference{max-width:48%}
              .courier-slip table,.courier-slip tbody{display:block}.courier-slip thead,.courier-slip colgroup{display:none}
              .courier-slip tbody tr{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));margin-top:5px;border:1px solid #dbe1ea;border-radius:5px;overflow:hidden}
              .courier-slip td{display:block;border:0;min-width:0}.courier-slip td:first-child{grid-column:1/-1;background:#f8fafc}
              .courier-slip td[data-label]{width:auto;text-align:left;font-size:12px;white-space:normal;overflow-wrap:anywhere}
              .courier-slip td[data-label]::before{content:attr(data-label);display:block;font-size:8px;color:#475569;margin-bottom:2px}
              .courier-slip .courier-totals{display:grid;grid-template-columns:repeat(2,minmax(0,1fr))}.courier-slip .courier-totals div{justify-content:space-between}
            }
            @media print{
              @page{size:A4 portrait;margin:10mm}
              .courier-slip{border:0;border-radius:0;padding:0;margin:0 0 3mm;font-size:8.5pt;line-height:1.25;break-inside:avoid;page-break-inside:avoid}
              .courier-slip:last-child{margin-bottom:0}
              .courier-slip table{font-size:8.5pt}
              .courier-slip thead{display:table-header-group}.courier-slip tr{break-inside:avoid;page-break-inside:avoid}
              .courier-slip header,.courier-slip .courier-recipient{break-inside:avoid}
              .courier-slip .courier-sender{overflow-wrap:anywhere;white-space:pre-line}
            }
          `}</style>
            {slips.map((slip, index) => (
              <section
                key={slip.id}
                className="courier-slip"
                aria-label={`Packing slip ${slip.number}`}
              >
                <header>
                  <BrandLogo width={28} height={28} className="h-7 w-7 shrink-0" />
                  <div className="courier-brand">
                    <h3>{brandConfig.name}</h3>
                    <p>{brandConfig.tagline}</p>
                  </div>
                  <div className="courier-reference">
                    <strong>Order {slip.number}</strong>
                    <p>
                      {saleDateLabel(slip.date)} ·{' '}
                      {saleStatusLabel(slip.shipment?.stage || slip.status)}
                    </p>
                  </div>
                </header>
                <p className="courier-sender">
                  From:{' '}
                  {slip.sender
                    ? `${slip.sender.name}, ${slip.sender.address} · ${slip.sender.phone}`
                    : `${brandConfig.address}, ${brandConfig.cityStateZip} · ${brandConfig.phone}`}
                </p>
                {slip.shipment && (slip.shipment.courier || slip.shipment.tracking) && (
                  <p className="courier-sender">
                    Courier: {slip.shipment.courier || 'Local delivery'}
                    {slip.shipment.tracking ? ` · Tracking: ${slip.shipment.tracking}` : ''}
                  </p>
                )}
                <div className="courier-recipient">
                  <div className="courier-contact">
                    <p>
                      Deliver to:{' '}
                      <strong className="courier-name">
                        {slip.recipient.name || 'Not recorded'}
                      </strong>
                    </p>
                    <p>
                      Phone:{' '}
                      <span data-customer-phone>{slip.recipient.phone || 'Not recorded'}</span>
                    </p>
                  </div>
                  <address>{slip.recipient.address || 'Not recorded'}</address>
                </div>
                <table>
                  <colgroup>
                    <col className="courier-product" />
                    <col className="courier-qty" />
                    <col className="courier-money" />
                    <col className="courier-money" />
                    <col className="courier-money" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="courier-product">Items for {slip.number}</th>
                      <th className="courier-qty">Qty</th>
                      <th className="courier-money">Unit price</th>
                      <th className="courier-money">Discount</th>
                      <th className="courier-money">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slip.items.length ? (
                      slip.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.name}</strong>
                            <div className="courier-variant">
                              {[item.size, item.color].filter(Boolean).join(' | ')}
                            </div>
                            {item.returnedQuantity > 0 && (
                              <div className="courier-variant">
                                Returned: {item.returnedQuantity}
                              </div>
                            )}
                          </td>
                          <td className="courier-qty" data-label="Qty">
                            {item.quantity}
                          </td>
                          <td className="courier-money" data-label="Unit price">
                            {saleMoney(item.unitPrice)}
                          </td>
                          <td className="courier-money" data-label="Discount">
                            {saleMoney(item.discount)}
                          </td>
                          <td className="courier-money" data-label="Amount">
                            {saleMoney(item.total)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>No items recorded</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <dl className="courier-totals">
                  <div>
                    <dt>Subtotal</dt>
                    <dd>{saleMoney(slip.subtotal)}</dd>
                  </div>
                  <div>
                    <dt>Discount</dt>
                    <dd>{saleMoney(slip.discount)}</dd>
                  </div>
                  <div>
                    <dt>Delivery</dt>
                    <dd>{saleMoney(slip.deliveryCharge)}</dd>
                  </div>
                  <div className="courier-grand-total">
                    <dt>Total</dt>
                    <dd>{saleMoney(slip.total)}</dd>
                  </div>
                </dl>
                <footer>Order {slip.number} · Packing slip · Not proof of payment</footer>
                {index < slips.length - 1 && (
                  <div className="courier-cut" aria-hidden="true">
                    Cut here
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>
        <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={printing}
            className="min-h-11 rounded-xl border border-border px-4 text-sm disabled:opacity-50"
          >
            Back to selection
          </button>
          <button
            type="button"
            onClick={print}
            disabled={printing}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Printer className="h-4 w-4" />
            {printing ? 'Opening print…' : 'Print packing slips'}
          </button>
        </footer>
      </WorkModalShell>
      {privacyDialog}
    </>
  );
}
